/**
 * Workflow API Client
 *
 * API functions for workflow management
 */

import { autoRefreshToken } from './auth'

// Use relative URLs by default (Vite proxy handles routing to backend)
// For production builds, set VITE_API_BASE_URL to the deployed backend base path.
const API_BASE = import.meta.env.VITE_API_BASE_URL || '/api';

/**
 * Get JWT Bearer token for API authentication
 * Auto-refreshes token if expired
 */
const getAuthHeader = async (): Promise<string> => {
  const accessToken = await autoRefreshToken()

  if (!accessToken) {
    throw new Error('Not authenticated')
  }

  return `Bearer ${accessToken}`
};

export interface WorkflowInfo {
  exists: boolean;
  name?: string;
  description?: string;
  imported_at?: string;
  file_size?: number;
  code_preview?: string;
}

export interface WorkflowHistoryItem {
  filename: string;
  timestamp: string;
  file_size: number;
}

export interface WorkflowImportRequest {
  code: string;
  name?: string;
  description?: string;
}

export interface WorkflowImportResponse {
  success: boolean;
  message: string;
  backup_created: boolean;
  file_path?: string;
}

/**
 * Get current workflow info
 */
export async function getCurrentWorkflow(): Promise<WorkflowInfo> {
  const authHeader = await getAuthHeader()
  const response = await fetch(`${API_BASE}/admin/workflows/current`, {
    headers: {
      'Authorization': authHeader,
    },
  });

  if (!response.ok) {
    throw new Error(`Failed to get current workflow: ${response.statusText}`);
  }

  return response.json();
}

/**
 * Import new workflow
 */
export async function importWorkflow(data: WorkflowImportRequest): Promise<WorkflowImportResponse> {
  const authHeader = await getAuthHeader()
  const response = await fetch(`${API_BASE}/admin/workflows/import`, {
    method: 'POST',
    headers: {
      'Authorization': authHeader,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || `Failed to import workflow: ${response.statusText}`);
  }

  return response.json();
}

/**
 * Get workflow history
 */
export async function getWorkflowHistory(): Promise<WorkflowHistoryItem[]> {
  const authHeader = await getAuthHeader()
  const response = await fetch(`${API_BASE}/admin/workflows/history`, {
    headers: {
      'Authorization': authHeader,
    },
  });

  if (!response.ok) {
    throw new Error(`Failed to get workflow history: ${response.statusText}`);
  }

  return response.json();
}

/**
 * Delete current workflow
 */
export async function deleteCurrentWorkflow(): Promise<{ success: boolean; message: string }> {
  const authHeader = await getAuthHeader()
  const response = await fetch(`${API_BASE}/admin/workflows/current`, {
    method: 'DELETE',
    headers: {
      'Authorization': authHeader,
    },
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || `Failed to delete workflow: ${response.statusText}`);
  }

  return response.json();
}
